import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { sortCountDesc } from './dashboardAnalytics';

const PDF_ORIENTATION = 'landscape';
const PDF_FORMAT = 'a4';
const CAPTURE_WIDTH_PX = 1400;
const CAPTURE_SCALE = 3;
const PDF_MARGIN_MM = 10;
const PANELS_PER_ROW = 3;
const ROW_GAP_MM = 5;
const COL_GAP_MM = 4;

export const CHART_MODE_LABELS = {
  bar: 'Bar chart',
  pie: 'Pie chart',
  combo: 'Combo (pie + bar)'
};

function sanitizeFilePart(value) {
  return String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function toTitleCaseWords(value) {
  return String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function buildPdfFileName({ companyName, reportName }) {
  const companyPart = sanitizeFilePart(toTitleCaseWords(companyName || 'Paytiemp'));
  const reportPart = sanitizeFilePart(toTitleCaseWords(reportName || 'Demographics Report'));
  const datePart = new Date().toISOString().slice(0, 10);
  return `${companyPart} - ${reportPart} - ${datePart}.pdf`;
}

/** Criteria mirrored from dashboard "Candidate Demographics & Decision Insights". */
export const DEMOGRAPHIC_REPORT_CRITERIA = [
  { dataKey: 'ageBands', title: 'Age Band', subtitle: 'All candidates by age group' },
  { dataKey: 'experienceBands', title: 'Experience Band', subtitle: 'Years of experience across all candidates' },
  { dataKey: 'fresherSplit', title: 'Fresher vs Experienced', subtitle: 'Candidate seniority split (full database)' },
  { dataKey: 'education', title: 'Education', subtitle: 'Highest qualification across all candidates', topN: 20 },
  { dataKey: 'expertise', title: 'Area of Expertise', subtitle: 'Domain distribution for decision-making', topN: 20 },
  { dataKey: 'coreCompetencies', title: 'Core Competencies', subtitle: 'Core competencies across all candidates', topN: 20 },
  { dataKey: 'technicalSkills', title: 'Technical Skills', subtitle: 'Technical skills across all candidates', topN: 20 },
  { dataKey: 'locations', title: 'Location', subtitle: 'Geographic spread of all candidates', topN: 20 },
  { dataKey: 'gender', title: 'Gender Split', subtitle: 'Diversity overview (all candidates)' }
];

function drawCriteriaColumn(doc, sections, startIndex, x, y, maxWidth) {
  let cursorY = y;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);

  for (const [offset, section] of sections.entries()) {
    const line = `${startIndex + offset + 1}. ${section.title} — ${section.subtitle}`;
    const wrapped = doc.splitTextToSize(line, maxWidth);
    doc.text(wrapped, x, cursorY);
    cursorY += wrapped.length * 4 + 2;
  }
  return cursorY;
}

function drawCoverPage(doc, { companyName, reportName, roleScope, chartMode, demographics, margin }) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = margin;

  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, pageWidth, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(sanitizeFilePart(companyName || 'Paytiemp'), margin, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(reportName, margin, 21);

  y = 36;
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(9);
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
  y += 5;
  doc.text('Format: A4 Landscape — high-resolution chart capture', margin, y);
  y += 5;
  if (roleScope) {
    doc.text(`Scope: ${roleScope}`, margin, y);
    y += 5;
  }
  doc.text(`Candidates in database: ${demographics.candidateCount ?? demographics.pipelineCount}`, margin, y);
  if (demographics.withApplicationsCount > 0) {
    y += 5;
    doc.text(`With applications: ${demographics.withApplicationsCount}`, margin, y);
  }
  y += 5;
  if (chartMode && CHART_MODE_LABELS[chartMode]) {
    doc.text(`Chart view: ${CHART_MODE_LABELS[chartMode]}`, margin, y);
    y += 5;
  }

  y += 3;
  doc.setDrawColor(203, 213, 225);
  doc.line(margin, y, pageWidth - margin, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text('Report criteria included in this export', margin, y);
  y += 7;

  const colGap = 8;
  const colWidth = (pageWidth - margin * 2 - colGap) / 2;
  const leftCol = DEMOGRAPHIC_REPORT_CRITERIA.slice(0, 5);
  const rightCol = DEMOGRAPHIC_REPORT_CRITERIA.slice(5);
  const leftEnd = drawCriteriaColumn(doc, leftCol, 0, margin, y, colWidth);
  const rightEnd = drawCriteriaColumn(doc, rightCol, 5, margin + colWidth + colGap, y, colWidth);
  y = Math.max(leftEnd, rightEnd) + 4;

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(
    'Following pages reproduce the dashboard charts exactly as displayed (pie, bar, and combo views).',
    margin,
    Math.min(y, pageHeight - margin - 4)
  );
}

function addPageFooters(doc, reportName) {
  const pageCount = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`${reportName} — Page ${page} of ${pageCount}`, pageWidth / 2, pageHeight - 6, {
      align: 'center'
    });
  }
}

function temporarilyExpandForCapture(element) {
  const nodes = [
    element,
    ...element.querySelectorAll('.overflow-y-auto, .overflow-hidden, .overflow-x-hidden')
  ];
  const snapshots = nodes.map(node => ({
    node,
    overflow: node.style.overflow,
    maxHeight: node.style.maxHeight,
    height: node.style.height,
    width: node.style.width,
    maxWidth: node.style.maxWidth,
    minWidth: node.style.minWidth
  }));

  snapshots.forEach(({ node }) => {
    node.style.overflow = 'visible';
    node.style.maxHeight = 'none';
    node.style.height = 'auto';
  });

  return snapshots;
}

function restoreCaptureStyles(snapshots) {
  snapshots.forEach(({ node, overflow, maxHeight, height, width, maxWidth, minWidth }) => {
    node.style.overflow = overflow;
    node.style.maxHeight = maxHeight;
    node.style.height = height;
    node.style.width = width;
    node.style.maxWidth = maxWidth;
    node.style.minWidth = minWidth;
  });
}

function waitForLayout(ms = 100) {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, ms);
      });
    });
  });
}

async function captureNode(element, { width = CAPTURE_WIDTH_PX } = {}) {
  const expanded = temporarilyExpandForCapture(element);
  await waitForLayout(60);

  try {
    return await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: CAPTURE_SCALE,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      width: element.scrollWidth || width,
      height: element.scrollHeight,
      windowWidth: width,
      windowHeight: element.scrollHeight,
      onclone: (_doc, clone) => {
        clone.style.background = '#ffffff';
        clone.querySelectorAll('[data-pdf-hide]').forEach(node => {
          node.style.display = 'none';
        });
        clone.querySelectorAll('svg').forEach(svg => {
          svg.setAttribute('shape-rendering', 'geometricPrecision');
        });
        clone.querySelectorAll('.overflow-y-auto, .overflow-hidden').forEach(node => {
          node.style.overflow = 'visible';
          node.style.maxHeight = 'none';
        });
      }
    });
  } finally {
    restoreCaptureStyles(expanded);
  }
}

function chunkPanels(panels, perRow = PANELS_PER_ROW) {
  const rows = [];
  for (let i = 0; i < panels.length; i += perRow) {
    rows.push(panels.slice(i, i + perRow));
  }
  return rows;
}

async function capturePanelRow(rowElements) {
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.style.width = `${CAPTURE_WIDTH_PX}px`;
  host.style.background = '#ffffff';
  host.style.display = 'grid';
  host.style.gridTemplateColumns = `repeat(${rowElements.length}, minmax(0, 1fr))`;
  host.style.gap = '12px';
  host.style.padding = '8px';
  host.style.zIndex = '-1';

  const clones = rowElements.map(el => {
    const clone = el.cloneNode(true);
    clone.style.width = '100%';
    clone.querySelectorAll('[data-pdf-hide]').forEach(node => {
      node.style.display = 'none';
    });
    return clone;
  });

  clones.forEach(clone => host.appendChild(clone));
  document.body.appendChild(host);
  await waitForLayout(120);

  try {
    return await captureNode(host, { width: CAPTURE_WIDTH_PX });
  } finally {
    document.body.removeChild(host);
  }
}

async function addChartPages(doc, reportElement, { margin }) {
  const header = reportElement.querySelector('[data-demographics-pdf-header]');
  const panels = [...reportElement.querySelectorAll('[data-demographic-chart-panel]')];

  if (!panels.length) {
    throw new Error('No demographic chart panels found');
  }

  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentHeight = pageHeight - margin * 2;

  doc.addPage();
  let cursorY = margin;

  if (header) {
    const headerCanvas = await captureNode(header, { width: CAPTURE_WIDTH_PX });
    const headerAspect = headerCanvas.height / headerCanvas.width;
    const contentWidth = pageWidth - margin * 2;
    let headerH = contentWidth * headerAspect;
    const maxHeaderH = contentHeight * 0.28;
    if (headerH > maxHeaderH) headerH = maxHeaderH;

    doc.addImage(
      headerCanvas.toDataURL('image/png'),
      'PNG',
      margin,
      cursorY,
      contentWidth,
      headerH,
      undefined,
      'FAST'
    );
    cursorY += headerH + ROW_GAP_MM;
  }

  const rows = chunkPanels(panels, PANELS_PER_ROW);

  for (const row of rows) {
    const rowCanvas = await capturePanelRow(row);
    const contentWidth = pageWidth - margin * 2;
    const rowAspect = rowCanvas.height / rowCanvas.width;
    const rowHeight = contentWidth * rowAspect;
    const spaceLeft = pageHeight - margin - cursorY;

    if (rowHeight > spaceLeft) {
      doc.addPage();
      cursorY = margin;
    }

    doc.addImage(
      rowCanvas.toDataURL('image/png'),
      'PNG',
      margin,
      cursorY,
      contentWidth,
      rowHeight,
      undefined,
      'FAST'
    );
    cursorY += rowHeight + ROW_GAP_MM;

    if (cursorY > pageHeight - margin - 40) {
      doc.addPage();
      cursorY = margin;
    }
  }
}

/**
 * Export the demographics section as a landscape PDF with charts rendered exactly as on screen.
 */
export async function exportDemographicsPdf({
  reportElement,
  demographics,
  companyName,
  reportName = 'Candidate Demographics & Decision Insights',
  roleScope,
  chartMode
}) {
  if (!(demographics?.candidateCount ?? demographics?.pipelineCount)) {
    alert('No pipeline demographic data to export.');
    return;
  }
  if (!reportElement) {
    alert('Report section is not ready. Please try again.');
    return;
  }

  const scrollParent = reportElement.closest('.overflow-y-auto');
  const previousScrollTop = scrollParent?.scrollTop ?? 0;
  reportElement.scrollIntoView({ block: 'start', behavior: 'auto' });
  await waitForLayout(150);

  const hidden = [...reportElement.querySelectorAll('[data-pdf-hide]')];
  const previousVisibility = hidden.map(node => node.style.visibility);
  hidden.forEach(node => {
    node.style.visibility = 'hidden';
  });

  try {
    const doc = new jsPDF({ orientation: PDF_ORIENTATION, unit: 'mm', format: PDF_FORMAT });
    const margin = PDF_MARGIN_MM;

    drawCoverPage(doc, {
      companyName,
      reportName,
      roleScope,
      chartMode,
      demographics,
      margin
    });

    await addChartPages(doc, reportElement, { margin });
    addPageFooters(doc, reportName);

    doc.save(
      buildPdfFileName({
        companyName,
        reportName: 'Candidate Demographics Report'
      })
    );
  } finally {
    hidden.forEach((node, index) => {
      node.style.visibility = previousVisibility[index] || '';
    });
    if (scrollParent) scrollParent.scrollTop = previousScrollTop;
  }
}

/** @deprecated kept for tests — table-only export no longer used in UI */
export function demographicsCountsForSection(demographics, section) {
  const raw = sortCountDesc(demographics?.[section.dataKey] || {});
  if (section.topN) {
    return Object.fromEntries(Object.entries(raw).slice(0, section.topN));
  }
  return raw;
}
